import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { toast } from 'sonner';
import { PageHead } from '@/admin-area/ui/PageHead';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { listPeople, type Person } from '../capture/api';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import { listReservedPurposes, setReservedPeople, type ReservedPurpose } from './api';

type Loaded =
  | { readonly status: 'loading' | 'failed' }
  | {
      readonly status: 'ready';
      readonly purposes: readonly ReservedPurpose[];
      readonly people: readonly Person[];
    };

/**
 * «Motivazioni riservate»: Gestione chooses, among the organisation's people, who may use each
 * restricted purpose (M17, M18). Until someone is chosen, nobody uses it. Audited by the server.
 */
export function ReservedPurposesPage() {
  const permissions = useExpensesPermissions();
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE)) return <Navigate to="/expenses" replace />;
  return <ReservedPurposes />;
}

function ReservedPurposes() {
  const { t } = useTranslation('expenses');
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  // Bumped by «Riprova»: a failed read (the purposes or the people list) is asked again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    void Promise.all([listReservedPurposes(), listPeople()]).then(([purposes, people]) => {
      if (!live) return;
      setLoaded(
        purposes.ok && people.ok
          ? { status: 'ready', purposes: purposes.value, people: people.value }
          : { status: 'failed' },
      );
    });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <section aria-label={t('reserved.title')} className="flex max-w-xl flex-col gap-6">
      <PageHead title={t('reserved.title')} summary={t('reserved.summary')} />
      {loaded.status === 'loading' ? <p role="status">{t('reserved.loading')}</p> : null}
      {loaded.status === 'failed' ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert">{t('reserved.loadFailed')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setLoaded({ status: 'loading' });
              setAttempt((n) => n + 1);
            }}
          >
            {t('reserved.retry')}
          </Button>
        </div>
      ) : null}
      {loaded.status === 'ready' && loaded.purposes.length === 0 ? (
        <p>{t('reserved.none')}</p>
      ) : null}
      {loaded.status === 'ready' && loaded.purposes.length > 0 ? (
        <ReservedForm purposes={loaded.purposes} people={loaded.people} />
      ) : null}
    </section>
  );
}

type Chosen = Readonly<Record<string, ReadonlySet<string>>>;

const idsOf = (purpose: ReservedPurpose): ReadonlySet<string> =>
  new Set(purpose.people.map((person) => person.userId));

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

/**
 * One form for the page, one «Salva» (Brand Kit: one primary button per view): it sends the
 * purposes whose people changed, each as its own audited write. The search narrows the people
 * shown in every purpose without touching what is ticked.
 */
function ReservedForm({
  purposes,
  people,
}: {
  readonly purposes: readonly ReservedPurpose[];
  readonly people: readonly Person[];
}) {
  const { t } = useTranslation('expenses');
  const searchId = useId();
  const [saved, setSaved] = useState<readonly ReservedPurpose[]>(purposes);
  const [chosen, setChosen] = useState<Chosen>(
    Object.fromEntries(purposes.map((purpose) => [purpose.code, idsOf(purpose)])),
  );
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // A second submit before React re-renders the disabled button is refused here, not sent twice.
  const inFlight = useRef(false);

  const changed = saved.filter((purpose) => !sameSet(chosen[purpose.code]!, idsOf(purpose)));
  const needle = search.trim().toLocaleLowerCase();
  const shown = people.filter((person) => person.displayName.toLocaleLowerCase().includes(needle));

  const toggle = (code: string, userId: string): void => {
    const next = new Set(chosen[code]);
    if (!next.delete(userId)) next.add(userId);
    setChosen({ ...chosen, [code]: next });
  };

  const save = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setErrors({});
    const done: ReservedPurpose[] = [];
    const failed: Record<string, string> = {};
    for (const purpose of changed) {
      const outcome = await setReservedPeople(purpose.code, [...chosen[purpose.code]!]);
      if (outcome.ok) done.push(outcome.value);
      else failed[purpose.code] = t(`reserved.problems.${outcome.code}`);
    }
    inFlight.current = false;
    setSaving(false);
    setErrors(failed);
    if (done.length === 0) return;
    setSaved((current) => current.map((p) => done.find((d) => d.code === p.code) ?? p));
    setChosen((current) => ({
      ...current,
      ...Object.fromEntries(done.map((purpose) => [purpose.code, idsOf(purpose)])),
    }));
    toast.success(t('reserved.saved', { codes: done.map((purpose) => purpose.code).join(', ') }));
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={searchId} className="text-sm font-semibold">
          {t('reserved.searchPeople')}
        </label>
        <Input
          id={searchId}
          type="search"
          value={search}
          className="min-h-11"
          onChange={(event) => setSearch(event.target.value)}
        />
        {needle !== '' && shown.length === 0 ? (
          <p role="status" className="text-muted-foreground text-sm">
            {t('reserved.noPeopleMatch')}
          </p>
        ) : null}
      </div>
      {saved.map((purpose) => (
        <PurposeGroup
          key={purpose.code}
          purpose={purpose}
          chosen={chosen[purpose.code]!}
          people={shown}
          error={errors[purpose.code] ?? null}
          onToggle={(userId) => toggle(purpose.code, userId)}
        />
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={saving || changed.length === 0} aria-busy={saving}>
          {saving ? t('reserved.saving') : t('reserved.save')}
        </Button>
        {changed.length === 0 && !saving ? (
          <span className="text-muted-foreground text-sm">{t('reserved.noChanges')}</span>
        ) : null}
      </div>
    </form>
  );
}

function PurposeGroup({
  purpose,
  chosen,
  people,
  error,
  onToggle,
}: {
  readonly purpose: ReservedPurpose;
  readonly chosen: ReadonlySet<string>;
  readonly people: readonly Person[];
  readonly error: string | null;
  readonly onToggle: (userId: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  // The organisation's people, then those still named but no longer listed (so they can be removed).
  const gone = purpose.people.filter((person) => !person.active);
  const options = [
    ...people.map((person) => ({ userId: person.userId, name: person.displayName, active: true })),
    ...gone.map((person) => ({ userId: person.userId, name: '', active: false })),
  ];
  return (
    <div className="flex flex-col gap-2">
      <fieldset className="flex flex-col gap-1" aria-describedby={`${uid}-hint`}>
        <legend className="text-sm font-semibold">
          {purpose.code} · {purpose.label}
        </legend>
        <p id={`${uid}-hint`} className="text-muted-foreground text-sm">
          {chosen.size === 0 ? t('reserved.nobody') : t('reserved.hint')}
        </p>
        {options.map((option) => (
          <label key={option.userId} className="flex min-h-11 items-center gap-2 text-sm">
            <Checkbox
              checked={chosen.has(option.userId)}
              onChange={() => onToggle(option.userId)}
            />
            {option.active ? option.name : t('reserved.inactive')}
          </label>
        ))}
      </fieldset>
      {error === null ? null : (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
    </div>
  );
}
